import type { FilterSpec } from "@/components/data";
import { parseFilters, type FilterValues } from "@/lib/filters";

import { channelLabel, STORE_LABEL } from "./format";
import { cellKey, type EditableMap, type EditCell, type StrategyMap } from "./strategies";

/**
 * The Strategies tab's filters over the product-channel map (Damien, 31 Oct 2026: "can you add filters to this page"). The same
 * filter bar as the Price Run (`FilterBar`, its labels and its `f_` URL keys, so a filter set on one tab carries to the other),
 * less Location, which the map has no grain for, plus "Only rows with no strategy assigned". Product and Variant narrow the
 * rows, Channel and Store the lanes, Marketplace both (the Amazon lanes that sell there, the products listed there). Like the
 * Price Run (C1 v1.41), each dropdown offers only the values the other active filters leave.
 */
export type MapFilterField = "product" | "variant" | "channel" | "store" | "marketplace";
export const MAP_FILTER_FIELDS: MapFilterField[] = ["product", "variant", "channel", "store", "marketplace"];
export type MapFilters = Record<MapFilterField, string | null>;
const NO_FILTERS: MapFilters = { product: null, variant: null, channel: null, store: null, marketplace: null };
const LABEL: Record<MapFilterField, string> = { product: "Product", variant: "Variant", channel: "Channel", store: "Store", marketplace: "Marketplace" };
/** The checkbox's URL key: not an `f_` key, so it stays on this tab, as the Price Run's `pp_missing` does on its own. */
export const UNASSIGNED_KEY = "ps_unassigned";
export const MAP_FILTER_COPY = {
  onlyUnassigned: "Only rows with no strategy assigned",
  showing: (shown: number, total: number) => `${shown} of ${total} products`,
  noMatch: "No products match these filters.",
};

/** One C2 map row as the filters see it: the cell it belongs to and the value it offers for each filter. */
type Line = { cell: string; values: MapFilters };
function linesOf(maps: StrategyMap[]): Line[] {
  return maps.flatMap((m) => m.rows.map((row) => ({
    cell: cellKey(row.channel_location, `${row.product} · ${row.variant}`),
    values: {
      product: row.product, variant: row.variant, channel: channelLabel(row), store: STORE_LABEL[row.channel_location] ?? row.channel_location,
      marketplace: row.grain === "marketplace" ? row.marketplace_name ?? row.marketplace_id : null,
    },
  })));
}
const matches = (line: Line, filters: MapFilters) => MAP_FILTER_FIELDS.every((f) => !filters[f] || line.values[f] === filters[f]);

/** The values one filter offers, given the others. A value already chosen stays offered, so the dropdown can show it. */
export function mapOptions(maps: StrategyMap[], field: MapFilterField, filters: Partial<MapFilters> = {}): Array<{ value: string; label: string }> {
  const others: MapFilters = { ...NO_FILTERS, ...filters, [field]: null };
  const seen = new Set<string>();
  for (const line of linesOf(maps)) if (matches(line, others) && line.values[field]) seen.add(line.values[field] as string);
  if (filters[field]) seen.add(filters[field] as string);
  return [...seen].sort().map((value) => ({ value, label: value }));
}

/** The filters from the URL, each kept only if the map offers it at all: a Price Run value the map has no row for (a ship
 * country under Marketplace) reads as "all" here instead of emptying the map. */
export function mapFiltersFrom(maps: StrategyMap[], search: Record<string, unknown>): MapFilters {
  const raw: FilterValues = parseFilters(search);
  const out = { ...NO_FILTERS };
  for (const f of MAP_FILTER_FIELDS) {
    const value = typeof raw[f] === "string" ? (raw[f] as string) : null;
    out[f] = value && mapOptions(maps, f).some((o) => o.value === value) ? value : null;
  }
  return out;
}

export function mapFilterSpecs(maps: StrategyMap[], filters: MapFilters): FilterSpec[] {
  return MAP_FILTER_FIELDS.map((f) => ({ name: f, label: LABEL[f], kind: "choice", options: mapOptions(maps, f, filters), anyLabel: "All" }));
}

/** A strategised cell no strategy reaches: no lane strategy and, on the marketplace filtered to (else on any marketplace), no
 * strategy of its own. A lane that is not strategised (UAE) is never "unassigned": nothing can be assigned there. */
export function unassigned(cell: EditCell, marketplace: string | null = null): boolean {
  if (!cell.strategised || cell.strategyId !== null) return false;
  const slots = marketplace ? cell.marketplaces.filter((m) => m.marketplace_name === marketplace) : cell.marketplaces;
  return slots.length === 0 || slots.some((m) => m.ownId === null);
}

/** The map with only the lanes and products that hold a cell the filters keep. Cells themselves are untouched. */
export function filterMap(map: EditableMap, maps: StrategyMap[], filters: MapFilters, onlyUnassigned: boolean): EditableMap {
  const kept = new Set<string>();
  for (const line of linesOf(maps)) {
    const cell = map.cells[line.cell];
    if (!cell || !matches(line, filters)) continue;
    if (onlyUnassigned && !unassigned(cell, filters.marketplace)) continue;
    kept.add(line.cell);
  }
  return {
    ...map,
    lanes: map.lanes.filter((l) => map.products.some((p) => kept.has(cellKey(l.key, p.key)))),
    products: map.products.filter((p) => map.lanes.some((l) => kept.has(cellKey(l.key, p.key)))),
  };
}
